import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
    Search, 
    Filter, 
    Calendar, 
    FileText, 
    CheckCircle, 
    AlertCircle, 
    Clock,
    Download,
    Eye
} from 'lucide-react';
import complianceApi from '../../api/complianceApi';
import { toast } from 'react-toastify';

const ComplianceTracker = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('ALL');
    const [filterCategory, setFilterCategory] = useState('ALL');
    const [complianceData, setComplianceData] = useState([]);
    const [loading, setLoading] = useState(true);
    const token = useSelector((state) => state.auth.access_token);
    const accounts = useSelector((state) => state.account.accountDetails);

    useEffect(() => {
        if (token && accounts?.length > 0) {
            fetchComplianceData();
        }
    }, [token, accounts]);

    const fetchComplianceData = async () => {
        try {
            setLoading(true);
            
            // Fetch documents for all accounts
            const allDocumentsPromises = accounts.map(account => 
                complianceApi.getDocumentsByAccount(account.id, token)
                    .catch(err => {
                        console.error(`Error fetching documents for account ${account.accountName}:`, err);
                        return [];
                    })
            );
            
            const allDocumentsArrays = await Promise.all(allDocumentsPromises);
            const allDocuments = allDocumentsArrays.flat();
            
            // Transform backend response to match frontend format
            const transformedData = allDocuments.map(doc => ({
                id: doc.id,
                accountName: doc.accountName,
                documentType: doc.documentType || doc.documentTypeOther || 'N/A',
                category: doc.complianceCategory,
                period: formatPeriod(doc),
                dueDate: doc.dueDate,
                status: doc.status,
                filedDate: doc.filedDate,
                remarks: doc.remarks || ''
            }));
            
            setComplianceData(transformedData);
        } catch (error) {
            console.error('Error fetching compliance data:', error);
            toast.error('Failed to load compliance documents');
        } finally {
            setLoading(false);
        }
    };

    const formatPeriod = (doc) => {
        if (doc.periodMonth) {
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${monthNames[doc.periodMonth - 1]} ${doc.fiscalYear || ''}`;
        }
        if (doc.periodQuarter) {
            return `Q${doc.periodQuarter} ${doc.fiscalYear || ''}`;
        }
        return doc.fiscalYear || 'N/A';
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            PENDING: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
            VERIFIED: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
            DISPUTED: { color: 'bg-red-100 text-red-800', icon: AlertCircle },
            BOOKED: { color: 'bg-purple-100 text-purple-800', icon: FileText },
            FILED: { color: 'bg-green-100 text-green-800', icon: CheckCircle }
        };

        const config = statusConfig[status] || statusConfig.PENDING;
        const Icon = config.icon;

        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${config.color}`}>
                <Icon size={14} />
                {status}
            </span>
        );
    };

    const getCategoryColor = (category) => {
        const colors = {
            GST: 'bg-blue-500',
            TDS: 'bg-green-500',
            INCOME_TAX: 'bg-purple-500',
            LEGAL: 'bg-orange-500',
            INTERNAL_ACCOUNTING: 'bg-gray-500'
        };
        return colors[category] || 'bg-gray-500';
    };

    const filteredData = complianceData.filter(item => {
        const matchesSearch = item.accountName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            item.documentType.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'ALL' || item.status === filterStatus;
        const matchesCategory = filterCategory === 'ALL' || item.category === filterCategory;
        
        return matchesSearch && matchesStatus && matchesCategory;
    });

    const stats = {
        total: complianceData.length,
        pending: complianceData.filter(d => d.status === 'PENDING').length,
        filed: complianceData.filter(d => d.status === 'FILED').length,
        overdue: complianceData.filter(d => {
            return d.status === 'PENDING' && new Date(d.dueDate) < new Date();
        }).length
    };

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Compliance Tracker</h1>
                    <p className="text-gray-600 mt-1">Monitor and track all compliance documents</p>
                </div>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2">
                    <Download size={16} />
                    Export Report
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Total Documents</p>
                            <p className="text-2xl font-bold">{stats.total}</p>
                        </div>
                        <FileText className="text-blue-500" size={32} />
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Pending</p>
                            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                        </div>
                        <Clock className="text-yellow-500" size={32} />
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Filed</p>
                            <p className="text-2xl font-bold text-green-600">{stats.filed}</p>
                        </div>
                        <CheckCircle className="text-green-500" size={32} />
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Overdue</p>
                            <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
                        </div>
                        <AlertCircle className="text-red-500" size={32} />
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                type="text"
                                placeholder="Search by account or document type..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>

                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="ALL">All Status</option>
                        <option value="PENDING">Pending</option>
                        <option value="VERIFIED">Verified</option>
                        <option value="DISPUTED">Disputed</option>
                        <option value="BOOKED">Booked</option>
                        <option value="FILED">Filed</option>
                    </select>

                    <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="ALL">All Categories</option>
                        <option value="GST">GST</option>
                        <option value="TDS">TDS</option>
                        <option value="INCOME_TAX">Income Tax</option>
                        <option value="LEGAL">Legal</option>
                        <option value="INTERNAL_ACCOUNTING">Internal Accounting</option>
                    </select>
                </div>
            </div>

            {/* Compliance Table */}
            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">Compliance Documents</h2>
                </div>
                <div className="p-6">
                    {loading ? (
                        <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-4 text-gray-600">Loading compliance data...</p>
                        </div>
                    ) : filteredData.length === 0 ? (
                        <div className="text-center py-8">
                            <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                            <p className="text-gray-600">No compliance documents found</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Account
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Category
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Document Type
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Period
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Due Date
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredData.map((item) => (
                                        <tr key={item.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="font-medium text-gray-900">{item.accountName}</div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 text-xs font-semibold text-white rounded ${getCategoryColor(item.category)}`}>
                                                    {item.category.replace('_', ' ')}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {item.documentType}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {item.period}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                                                <div className="flex items-center gap-1">
                                                    <Calendar size={14} className="text-gray-400" />
                                                    {new Date(item.dueDate).toLocaleDateString()}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                {getStatusBadge(item.status)}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm">
                                                <button className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-md flex items-center gap-1">
                                                    <Eye size={16} />
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ComplianceTracker;
